import * as z from "zod";

import {
  postAuthJson,
  readAuthErrorCode,
  readTokenResponse,
} from "@/features/auth/session/auth-http";

import type { SsoAuthenticationMethodOut } from "@/api-client/types.gen";
import type { TokenResponse } from "@/features/auth/session/session-store";

export type SsoMethodId = SsoAuthenticationMethodOut["id"];

export type PendingSso = {
  readonly codeVerifier: string;
  readonly methodId: SsoMethodId;
  readonly redirectUri: string;
  readonly startedAt: number;
};

export type SsoExchangeResult =
  | { readonly ok: true; readonly tokens: TokenResponse }
  | { readonly ok: false; readonly code: string };

export const SSO_SERVICE_UNAVAILABLE_CODE = "service_unavailable";

const PENDING_SSO_KEY = "trux-sso-pending";
const PENDING_SSO_TTL_MS = 10 * 60 * 1000;
const SSO_EXCHANGE_PATH = "/api/v1/auth/sso/exchange/";
const LOGIN_CODE_TTL_MS = 120_000;
const EXCHANGE_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];
const EXCHANGE_RETRY_INTERVAL_MS = 15_000;

const pendingSsoSchema = z.object({
  codeVerifier: z.string().regex(/^[\w.~-]{43,128}$/),
  methodId: z.enum(["nextretail_sso", "retailer_sso"]),
  redirectUri: z.string(),
  startedAt: z.number(),
});

function toBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) {
    binary += String.fromCodePoint(byte);
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function createCodeVerifier(): string {
  return toBase64Url(crypto.getRandomValues(new Uint8Array(32)));
}

export async function createCodeChallenge(codeVerifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(codeVerifier));
  return toBase64Url(new Uint8Array(digest));
}

export function ssoRedirectUri(): string {
  return `${window.location.origin}/login`;
}

function withStorage<T>(action: () => T): T | undefined {
  try {
    return action();
  } catch {
    return undefined;
  }
}

export function savePendingSso(pending: PendingSso): boolean {
  return (
    withStorage(() => {
      sessionStorage.setItem(PENDING_SSO_KEY, JSON.stringify(pending));
      return true;
    }) === true
  );
}

export function readPendingSso(now = Date.now()): PendingSso | null {
  const pending = withStorage(() => {
    const raw = sessionStorage.getItem(PENDING_SSO_KEY);
    return raw === null ? null : pendingSsoSchema.safeParse(JSON.parse(raw)).data;
  });
  if (!pending) {
    return null;
  }
  const age = now - pending.startedAt;
  return age >= 0 && age < PENDING_SSO_TTL_MS ? pending : null;
}

export function clearPendingSso() {
  withStorage(() => {
    sessionStorage.removeItem(PENDING_SSO_KEY);
  });
}

export function buildSsoStartUrl(
  startUrl: string,
  redirectUri: string,
  codeChallenge: string,
): string | null {
  const url = new URL(startUrl, window.location.origin);
  if (url.origin !== window.location.origin) {
    return null;
  }
  url.searchParams.set("redirect_uri", redirectUri);
  url.searchParams.set("code_challenge", codeChallenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.toString();
}

export async function startSso(
  method: SsoAuthenticationMethodOut,
  navigate: (url: string) => void = (url) => {
    window.location.assign(url);
  },
): Promise<boolean> {
  const codeVerifier = createCodeVerifier();
  const redirectUri = ssoRedirectUri();
  const startUrl = buildSsoStartUrl(
    method.start_url,
    redirectUri,
    await createCodeChallenge(codeVerifier),
  );
  if (startUrl === null) {
    return false;
  }
  const saved = savePendingSso({
    codeVerifier,
    methodId: method.id,
    redirectUri,
    startedAt: Date.now(),
  });
  if (!saved) {
    return false;
  }
  navigate(startUrl);
  return true;
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

function withJitter(ms: number): number {
  return ms + Math.random() * ms * 0.2;
}

async function attemptExchange(loginCode: string, codeVerifier: string) {
  let response: Response;
  try {
    response = await postAuthJson(SSO_EXCHANGE_PATH, {
      login_code: loginCode,
      code_verifier: codeVerifier,
    });
  } catch {
    return { ok: false, code: SSO_SERVICE_UNAVAILABLE_CODE } as const;
  }
  if (response.ok) {
    const tokens = await readTokenResponse(response);
    return tokens === null
      ? ({ ok: false, code: "internal_error" } as const)
      : ({ ok: true, tokens } as const);
  }
  if (response.status === 503) {
    return { ok: false, code: SSO_SERVICE_UNAVAILABLE_CODE } as const;
  }
  return { ok: false, code: (await readAuthErrorCode(response)) ?? "internal_error" } as const;
}

export async function exchangeLoginCode(
  loginCode: string,
  codeVerifier: string,
  receivedAt = performance.now(),
): Promise<SsoExchangeResult> {
  const deadline = receivedAt + LOGIN_CODE_TTL_MS;
  for (let attempt = 0; ; attempt += 1) {
    const result = await attemptExchange(loginCode, codeVerifier);
    if (result.ok || result.code !== SSO_SERVICE_UNAVAILABLE_CODE) {
      return result;
    }
    const delay = withJitter(EXCHANGE_RETRY_DELAYS_MS[attempt] ?? EXCHANGE_RETRY_INTERVAL_MS);
    if (performance.now() + delay >= deadline) {
      return result;
    }
    await wait(delay);
  }
}
